<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { formatDollars } from '@/utils/format';

const auth = useAuthStore();
const cart = useCartStore();
const route = useRoute();
const router = useRouter();

const balance = computed(() =>
  auth.user ? formatDollars(auth.user.walletAvailableCents) : '$0.00',
);

async function handleLogout() {
  drawerOpen.value = false;
  profileOpen.value = false;
  await auth.logout();
  router.push({ name: 'Login' });
}

// Mobile burger drawer state. Close on route change so navigation feels
// natural; the drawer itself nav-links call drawerOpen=false on click.
const drawerOpen = ref(false);

// Desktop profile dropdown state.
const profileOpen = ref(false);
const profileMenuRef = ref<HTMLElement | null>(null);

watch(() => route.fullPath, () => {
  drawerOpen.value = false;
  profileOpen.value = false;
});

function onDocClick(e: MouseEvent) {
  if (
    profileOpen.value &&
    profileMenuRef.value &&
    !profileMenuRef.value.contains(e.target as Node)
  ) {
    profileOpen.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') profileOpen.value = false;
}

onMounted(() => {
  document.addEventListener('click', onDocClick);
  window.addEventListener('keydown', onKeydown);
});
onUnmounted(() => {
  document.removeEventListener('click', onDocClick);
  window.removeEventListener('keydown', onKeydown);
});

// Public-folder asset paths (Vite serves /public/figma/* at /figma/*)
const a = (name: string) => `/figma/header/${name}`;

interface NavItem {
  to?: string;
  label: string;
  icon: string;
  matchPath?: string; // path prefix that lights this nav as active
  disabled?: boolean;
  action?: 'cart'; // renders a button that opens the cart popover instead of a link
}

const cartIcon = a('nav-cart.svg');

// Primary nav — the 8 tiles from the Figma header (Home, Marketplace, Blog,
// About, FAQ, Cart, Favorites, Contact). Cart opens the popover; the rest link.
const navItems: NavItem[] = [
  { to: '/',           label: 'home',        icon: a('nav-home.svg'),        matchPath: '/' },
  { to: '/catalog',    label: 'marketplace', icon: a('nav-marketplace.svg'), matchPath: '/catalog' },
  { to: '/blog',       label: 'blog',        icon: a('nav-blog.svg'),        matchPath: '/blog' },
  { to: '/about',      label: 'about us',    icon: a('nav-about.svg'),       matchPath: '/about' },
  { to: '/faq',        label: 'faq',         icon: a('nav-faq.svg'),         matchPath: '/faq' },
  { action: 'cart',    label: 'cart',        icon: cartIcon },
  { to: '/favorites',  label: 'favorites',   icon: a('nav-favorites.svg'),   matchPath: '/favorites' },
  { to: '/contact',    label: 'contact us',  icon: a('nav-contact.svg'),     matchPath: '/contact' },
];

// Extra authed links surfaced in the mobile drawer + profile dropdown.
const drawerTrailingItems = computed<NavItem[]>(() => {
  if (!auth.user) return [];
  return [
    { to: '/profile',   label: 'profile',          icon: a('profile.svg'),        matchPath: '/profile' },
    { to: '/inventory', label: 'my inventory',     icon: a('nav-marketplace.svg'), matchPath: '/inventory' },
    { to: '/history',   label: 'purchase history', icon: a('nav-blog.svg'),        matchPath: '/history' },
    { to: '/wallet',    label: 'wallet',           icon: a('balance-button.svg'),  matchPath: '/wallet' },
  ];
});
const steamIcon = a('steam-icon.svg');

function isActive(item: NavItem): boolean {
  if (!item.matchPath) return false;
  if (item.matchPath === '/') return route.path === '/';
  return route.path === item.matchPath || route.path.startsWith(item.matchPath + '/');
}
</script>

<template>
  <header class="sticky top-0 z-50 bg-[#080809]/95 backdrop-blur-md border-b border-[var(--color-border)] text-[var(--color-text)]">
    <div class="max-w-[1520px] mx-auto px-4 lg:px-8 h-16 lg:h-[88px] flex items-center gap-3 lg:gap-6">
      <!-- Mobile burger — only visible < lg. Opens the drawer. -->
      <button
        type="button"
        class="lg:hidden w-10 h-10 rounded-[10px] bg-[var(--color-surface-3)] border border-[var(--color-border-medium)] flex items-center justify-center text-[var(--color-text)] cursor-pointer shrink-0 hover:border-[var(--color-accent)] transition-colors"
        aria-label="Open menu"
        @click="drawerOpen = true"
      >
        <svg width="20" height="14" viewBox="0 0 20 14" fill="none" aria-hidden="true">
          <path d="M2 2H18M2 7H18M2 12H18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>

      <!-- Logo -->
      <RouterLink to="/" class="flex items-center no-underline shrink-0" aria-label="JuicySkins home">
        <img
          src="/figma/brand/juicyskins-logo.png"
          alt="JuicySkins"
          class="h-12 lg:h-20 w-auto select-none"
        />
      </RouterLink>

      <!-- Primary nav — desktop only, Epic Cherry stacked tiles (8 from Figma). -->
      <nav class="hidden lg:flex items-center gap-1 h-full">
        <template v-for="item in navItems" :key="item.label">
          <!-- Cart tile — opens the popover instead of navigating. -->
          <button
            v-if="item.action === 'cart'"
            type="button"
            aria-label="Open cart"
            class="relative flex flex-col items-center justify-center gap-2 px-4 py-3 rounded-[12px] shrink-0 border border-transparent bg-transparent cursor-pointer text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] transition-colors"
            @click="cart.toggle()"
          >
            <span class="relative inline-flex">
              <img :src="item.icon" alt="" aria-hidden="true" class="h-[22px] w-[22px]" />
              <span
                v-if="cart.count > 0"
                class="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--color-accent)] text-white font-display font-bold text-[10px] leading-[16px] text-center"
              >{{ cart.count }}</span>
            </span>
            <span class="font-display font-bold uppercase text-[11px] leading-[11px] tracking-[1px] whitespace-nowrap">{{ item.label }}</span>
          </button>
          <RouterLink
            v-else
            :to="item.to!"
            class="relative flex flex-col items-center justify-center gap-2 px-4 py-3 rounded-[12px] no-underline shrink-0 border transition-colors"
            :class="
              isActive(item)
                ? 'bg-[var(--color-accent-dim)] border-[var(--color-accent-ring)] text-[var(--color-text)]'
                : 'border-transparent text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)]'
            "
          >
            <img :src="item.icon" alt="" aria-hidden="true" class="h-[22px] w-[22px]" />
            <span class="font-display font-bold uppercase text-[11px] leading-[11px] tracking-[1px] whitespace-nowrap">{{ item.label }}</span>
          </RouterLink>
        </template>
      </nav>

      <div class="flex-1"></div>

      <!-- Right cluster -->
      <div class="flex items-center gap-2 lg:gap-3 shrink-0">
        <!-- Balance pill (auth only) — whole pill links to /wallet; the "+"
             is a visual top-up affordance, not a second nested link, since
             it targets the same destination. -->
        <RouterLink
          v-if="auth.user"
          to="/wallet"
          class="flex items-center gap-2 h-10 pl-4 pr-1.5 rounded-full border border-[var(--color-border-medium)] hover:border-[var(--color-accent)] no-underline transition-colors"
        >
          <img :src="a('balance-button.svg')" alt="" aria-hidden="true" class="h-4 w-auto" />
          <span class="font-display font-bold text-[13px] tracking-[0.6px] text-[var(--color-text)] whitespace-nowrap">{{ balance }}</span>
          <span
            class="w-7 h-7 rounded-full bg-gradient-to-r from-[var(--color-accent-from)] to-[var(--color-accent-to)] flex items-center justify-center shrink-0"
            aria-hidden="true"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 1V11M1 6H11" stroke="white" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </span>
        </RouterLink>

        <!-- Profile dropdown (auth, desktop only) -->
        <div v-if="auth.user" ref="profileMenuRef" class="relative hidden lg:block">
          <button
            type="button"
            class="flex items-center gap-2 h-10 pl-1.5 pr-3 rounded-full border border-[var(--color-border-medium)] hover:border-[var(--color-accent)] transition-colors bg-transparent cursor-pointer"
            :aria-expanded="profileOpen"
            aria-haspopup="true"
            aria-label="Account menu"
            @click="profileOpen = !profileOpen"
          >
            <img
              v-if="auth.user.avatarUrl"
              :src="auth.user.avatarUrl"
              :alt="auth.user.username"
              class="w-8 h-8 rounded-full ring-1 ring-[var(--color-border)]"
            />
            <img v-else :src="a('profile.svg')" alt="" aria-hidden="true" class="w-8 h-8 rounded-full bg-[var(--color-surface-3)] p-1.5" />
            <span class="hidden xl:inline font-display font-bold uppercase text-[13px] tracking-[0.6px] text-[var(--color-text)] truncate max-w-[120px]">
              {{ auth.user.username }}
            </span>
            <img
              :src="a('chevron-down.svg')"
              alt=""
              aria-hidden="true"
              class="w-2.5 h-2.5 opacity-70 transition-transform"
              :class="profileOpen ? 'rotate-180' : ''"
            />
          </button>

          <Transition name="dropdown">
            <div
              v-if="profileOpen"
              class="absolute right-0 mt-2 min-w-[220px] rounded-[14px] bg-[var(--color-surface-3)] border border-[var(--color-border-medium)] shadow-2xl overflow-hidden"
            >
              <div class="px-4 py-3 border-b border-[var(--color-border)]">
                <p class="font-display font-bold uppercase text-[14px] text-[var(--color-text)] truncate">{{ auth.user.username }}</p>
                <p v-if="auth.user.email" class="mt-0.5 font-body text-[12px] text-[var(--color-text-dim)] truncate">
                  {{ auth.user.email }}
                </p>
              </div>
              <nav class="py-1">
                <RouterLink
                  to="/profile"
                  class="flex items-center gap-3 px-4 py-2.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] no-underline transition-colors"
                  @click="profileOpen = false"
                >
                  <img :src="a('profile.svg')" alt="" aria-hidden="true" class="h-[18px] w-auto" />
                  <span class="font-display font-bold uppercase text-[13px] tracking-[0.6px]">Profile</span>
                </RouterLink>
                <RouterLink
                  to="/wallet"
                  class="flex items-center gap-3 px-4 py-2.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] no-underline transition-colors"
                  @click="profileOpen = false"
                >
                  <img :src="a('balance-button.svg')" alt="" aria-hidden="true" class="h-[18px] w-auto" />
                  <span class="font-display font-bold uppercase text-[13px] tracking-[0.6px]">Wallet</span>
                </RouterLink>
                <RouterLink
                  to="/inventory"
                  class="flex items-center gap-3 px-4 py-2.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] no-underline transition-colors"
                  @click="profileOpen = false"
                >
                  <img :src="a('nav-marketplace.svg')" alt="" aria-hidden="true" class="h-[18px] w-auto" />
                  <span class="font-display font-bold uppercase text-[13px] tracking-[0.6px]">My Inventory</span>
                </RouterLink>
                <RouterLink
                  to="/history"
                  class="flex items-center gap-3 px-4 py-2.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] no-underline transition-colors"
                  @click="profileOpen = false"
                >
                  <img :src="a('nav-blog.svg')" alt="" aria-hidden="true" class="h-[18px] w-auto" />
                  <span class="font-display font-bold uppercase text-[13px] tracking-[0.6px]">Purchase History</span>
                </RouterLink>
                <RouterLink
                  to="/favorites"
                  class="flex items-center gap-3 px-4 py-2.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] no-underline transition-colors"
                  @click="profileOpen = false"
                >
                  <img :src="a('nav-favorites.svg')" alt="" aria-hidden="true" class="h-[18px] w-auto" />
                  <span class="font-display font-bold uppercase text-[13px] tracking-[0.6px]">Favorites</span>
                </RouterLink>
              </nav>
              <div class="border-t border-[var(--color-border)] py-1">
                <button
                  type="button"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-[var(--color-error)] hover:bg-[var(--color-error-dim)] bg-transparent border-0 cursor-pointer transition-colors"
                  @click="handleLogout"
                >
                  <span
                    class="w-[18px] h-[18px] bg-current inline-block"
                    :style="{
                      maskImage: `url('${a('logout.png')}')`,
                      maskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      maskPosition: 'center',
                      WebkitMaskImage: `url('${a('logout.png')}')`,
                      WebkitMaskSize: 'contain',
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                    }"
                    aria-hidden="true"
                  />
                  <span class="font-display font-bold uppercase text-[13px] tracking-[0.6px]">Log out</span>
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Guest state -->
        <template v-if="!auth.user">
          <RouterLink
            to="/login"
            class="flex items-center justify-center px-4 py-2.5 lg:px-5 rounded-full border border-[var(--color-border-medium)] text-[var(--color-text)] no-underline font-display font-bold uppercase text-[12px] lg:text-[13px] tracking-[0.6px] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors whitespace-nowrap"
          >
            log in
          </RouterLink>
          <RouterLink
            to="/login?mode=signup"
            class="flex items-center justify-center px-4 py-2.5 lg:px-5 rounded-full text-white no-underline font-display font-bold uppercase text-[12px] lg:text-[13px] tracking-[0.6px] bg-gradient-to-r from-[var(--color-accent-from)] to-[var(--color-accent-to)] hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            sign up
          </RouterLink>
          <!-- Sign in with Steam -->
          <a
            href="/api/auth/steam/login"
            class="hidden lg:flex items-center justify-center gap-2.5 px-5 py-2.5 rounded-full text-white no-underline bg-[var(--color-steam-bg)] border border-[var(--color-steam-border)] font-display font-bold uppercase text-[12px] lg:text-[13px] tracking-[0.6px] hover:border-[var(--color-accent)] transition-colors whitespace-nowrap"
          >
            <img :src="steamIcon" alt="" aria-hidden="true" class="h-5 w-5" />
            Sign in with Steam
          </a>
        </template>
      </div>
    </div>

    <!-- Mobile drawer: slides in from the left. Teleported to body so it
         sits on top of everything regardless of where AppHeader is mounted.
         Backdrop closes it; route changes also auto-close (see watcher). -->
    <Teleport to="body">
      <div v-if="drawerOpen" class="lg:hidden fixed inset-0 z-[60]" role="dialog" aria-label="Menu">
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/70"
          @click="drawerOpen = false"
        />
        <!-- Panel -->
        <aside
          class="absolute left-0 top-0 bottom-0 w-[78vw] max-w-[320px] bg-[var(--color-surface-2)] border-r border-[var(--color-border-medium)] flex flex-col shadow-2xl"
        >
          <!-- Drawer header -->
          <div class="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[var(--color-border)]">
            <img src="/figma/brand/juicyskins-logo.png" alt="JuicySkins" class="h-12 w-auto select-none" />
            <button
              type="button"
              class="w-10 h-10 rounded-[10px] bg-transparent text-[var(--color-text-dim)] hover:text-[var(--color-text)] text-[24px] leading-none border-0 cursor-pointer"
              aria-label="Close menu"
              @click="drawerOpen = false"
            >
              ×
            </button>
          </div>

          <!-- Nav links -->
          <nav class="flex-1 overflow-y-auto py-2">
            <RouterLink
              v-for="item in [...navItems.filter((i) => !i.action), ...drawerTrailingItems]"
              :key="item.label"
              :to="item.disabled ? '' : (item.to ?? '')"
              :tabindex="item.disabled ? -1 : 0"
              :aria-disabled="item.disabled || undefined"
              class="flex items-center gap-3 px-5 py-3.5 no-underline transition-colors"
              :class="[
                item.disabled
                  ? 'text-[var(--color-text-faded)] pointer-events-none'
                  : isActive(item)
                    ? 'text-[var(--color-accent)] bg-[var(--color-accent-dim)]'
                    : 'text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)]',
              ]"
            >
              <img :src="item.icon" alt="" aria-hidden="true" class="h-[22px] w-[22px]" />
              <span class="font-display font-bold uppercase text-[14px] tracking-[0.6px] leading-none">{{ item.label }}</span>
            </RouterLink>

            <!-- Cart entry (opens the popover, closes the drawer) -->
            <button
              type="button"
              class="w-full flex items-center gap-3 px-5 py-3.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] bg-transparent border-0 cursor-pointer transition-colors"
              @click="drawerOpen = false; cart.toggle()"
            >
              <span class="relative h-[22px]">
                <img :src="cartIcon" alt="" aria-hidden="true" class="h-[22px] w-[22px]" />
                <span
                  v-if="cart.count > 0"
                  class="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--color-accent)] text-white font-display font-bold text-[10px] leading-[16px] text-center"
                >
                  {{ cart.count }}
                </span>
              </span>
              <span class="font-display font-bold uppercase text-[14px] tracking-[0.6px] leading-none">cart</span>
            </button>

            <!-- Guest links — mobile has no top-row login/signup, so mirror
                 them here in the drawer. -->
            <template v-if="!auth.user">
              <RouterLink
                to="/login"
                class="flex items-center gap-3 px-5 py-3.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] no-underline transition-colors"
              >
                <span class="font-display font-bold uppercase text-[14px] tracking-[0.6px] leading-none">log in</span>
              </RouterLink>
              <RouterLink
                to="/login?mode=signup"
                class="flex items-center gap-3 px-5 py-3.5 text-[var(--color-accent)] hover:bg-[var(--color-accent-dim)] no-underline transition-colors"
              >
                <span class="font-display font-bold uppercase text-[14px] tracking-[0.6px] leading-none">sign up</span>
              </RouterLink>
              <a
                href="/api/auth/steam/login"
                class="flex items-center gap-3 px-5 py-3.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)] hover:bg-[var(--color-accent-dim)] no-underline transition-colors"
              >
                <img :src="steamIcon" alt="" aria-hidden="true" class="h-5 w-5" />
                <span class="font-display font-bold uppercase text-[14px] tracking-[0.6px] leading-none">Sign in with Steam</span>
              </a>
            </template>
          </nav>

          <!-- Logout (red) — only when logged in. Lives at the bottom so it's
               last in the mental order (look here / nav / leave). -->
          <div
            v-if="auth.user"
            class="border-t border-[var(--color-border)] p-3"
          >
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-[10px] bg-transparent text-[var(--color-error)] hover:bg-[var(--color-error-dim)] font-display font-bold uppercase text-[14px] tracking-[0.6px] leading-none border-0 cursor-pointer transition-colors"
              @click="handleLogout"
            >
              <span
                class="w-[20px] h-[20px] bg-current inline-block"
                :style="{
                  maskImage: `url('${a('logout.png')}')`,
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  WebkitMaskImage: `url('${a('logout.png')}')`,
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                }"
                aria-hidden="true"
              />
              log out
            </button>
          </div>
        </aside>
      </div>
    </Teleport>
  </header>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: none;
  }
  .dropdown-enter-from,
  .dropdown-leave-to {
    transform: none;
  }
}
</style>
